import { Suspense, lazy, useEffect } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

// Lazy load toast components (not needed for initial render)
const Toaster = lazy(() => import("@/components/ui/toaster").then(m => ({ default: m.Toaster })));
const Sonner = lazy(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));
import { BrowserRouter, Routes, Route } from "react-router-dom";

// Lazy load all routes for better performance
const Index = lazy(() => import("./pages/Index"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Cookies = lazy(() => import("./pages/Cookies"));
const Contact = lazy(() => import("./pages/Contact"));
const About = lazy(() => import("./pages/About"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const DevOtp = lazy(() => import("./pages/DevOtp"));
const SolutionDetail = lazy(() => import("./pages/solutions/SolutionDetail"));
import CookieConsent from "./components/CookieConsent";
import GoogleAnalytics from "./components/seo/GoogleAnalytics";
import StructuredData from "./components/seo/StructuredData";
import ImpactModalRoot from "./components/solutions/ImpactModal";

const queryClient = new QueryClient();

const App = () => {
  useEffect(() => {
    // Señal para el prerender: Helmet + contenido listos
    (window as any).__VERAIS_PRERENDER_READY = true;
    document.dispatchEvent(new Event("verais:prerender-ready"));
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Suspense fallback={null}>
          <Toaster />
          <Sonner />
        </Suspense>
      
      {/* Global Structured Data */}
      <StructuredData type="organization" />
      <StructuredData type="website" />
      <StructuredData type="faq" />
      <StructuredData type="service" />
      <StructuredData type="localbusiness" />
      
      <BrowserRouter>
        <GoogleAnalytics />
        <Suspense fallback={
          <div className="min-h-screen flex items-center justify-center">
            <div className="animate-pulse text-muted-foreground">Cargando...</div>
          </div>
        }>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/cookies" element={<Cookies />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/about" element={<About />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/soluciones/:slug" element={<SolutionDetail />} />
            {import.meta.env.DEV && (
              <Route path="/dev/otp" element={<DevOtp />} />
            )}
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
        <CookieConsent />
        <ImpactModalRoot />
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  );
};

export default App;
